import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Card, CardBody, Col, Container, CardHeader, Row, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import TableContainer from "../../../Components/Common/TableContainer";
import { useSelector, useDispatch } from "react-redux";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import { setAuthorization } from "../../../helpers/api_helper";
import { getClientsOverdue as onGetClients, sendOverdueEmail, sendReminderEmail, logoutUser } from "../../../slices/thunks"; // Added logoutUser import
import Loader from "../../../Components/Common/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";

const formatDate = (dateString) => {
  if (!dateString) return "Never Sent"; // If null, display "Never Sent"

  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "long", year: "numeric" }).format(date);
};

const ClientManagement = () => {
  const dispatch = useDispatch();
  const { clients, isClientSuccess, error } = useSelector(
    (state) => state.Clients
  );

  // State for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setPageSize(5);
      } else {
        setPageSize(10);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { userProfile, loading, token } = useProfile();
  
  useEffect(() => {
    if (userProfile && !loading && token) {
      setAuthorization(token);
    } else if (!userProfile && !loading && !token) {
      dispatch(logoutUser());
    }
  }, [token, userProfile, loading, dispatch]);

  useEffect(() => {
    if (token) {
      dispatch(onGetClients());
    }
  }, [dispatch, token]);

  const contractsData = useMemo(() => {
    if (!clients || !Array.isArray(clients)) return [];

    return clients.flatMap((client) => {
      let contractEntries = [];

      if (!client.contracts || !Array.isArray(client.contracts)) {
        return [];
      }

      client.contracts.forEach((contract) => {
        if (contract.overdue_payments && contract.overdue_payments.length > 0) {
          contract.overdue_payments.forEach((payment) => {
            contractEntries.push({
              id: client.id,
              contract_id: contract.contract_id,
              full_name: `${client.name || ""} ${client.surname || ""}`.trim(),
              pvc_number: contract.pvc_number,
              unit_details: contract.unit ? contract.unit.map(u => `${u.project_name || ''} ${u.phase_name || ''} ${u.block_name || ''}`).join(", ") : "N/A",
              unit_count: contract.unit ? contract.unit.length : 0,
              status: `Overdue by ${payment.days_overdue} day${payment.days_overdue > 1 ? "s" : ""}`,
              actionType: "overdue",
              email: client.email,
              last_email_sent_at: formatDate(contract.last_email_sent_at),
              days: payment.days_overdue,
            });
          });
        }

        if (contract.upcoming_payments && contract.upcoming_payments.length > 0) {
          contract.upcoming_payments.forEach((payment) => {
            contractEntries.push({
              id: client.id,
              contract_id: contract.contract_id,
              full_name: `${client.name || ""} ${client.surname || ""}`.trim(),
              pvc_number: contract.pvc_number,
              unit_details: contract.unit ? contract.unit.map(u => `${u.project_name || ''} ${u.phase_name || ''} ${u.block_name || ''}`).join(", ") : "N/A",
              unit_count: contract.unit ? contract.unit.length : 0,
              status: `Due in: ${payment.days_until_due} day${payment.days_until_due > 1 ? "s" : ""} left`,
              actionType: "reminder",
              email: client.email,
              last_email_sent_at: formatDate(contract.last_email_sent_at),
              days: payment.days_until_due,
            });
          });
        }
      });
      return contractEntries;
    });
  }, [clients]);

  // Modal toggle function
  const toggleModal = useCallback(() => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setSelectedClient(null);
    }
  }, [modalOpen]);

  // Open modal with client data
  const openSendReminderModal = useCallback((client) => {
    setSelectedClient(client);
    setModalOpen(true);
  }, []);

  const handleSendEmail = async () => {
    if (!selectedClient) return;

    setIsSubmitting(true);

    try {
      let action;
      if (selectedClient.actionType === "overdue") {
        action = dispatch(sendOverdueEmail({
          name: selectedClient.full_name,
          email: selectedClient.email,
          days: selectedClient.days,
          pvc_number: selectedClient.pvc_number,
        }));
      } else if (selectedClient.actionType === "reminder") {
        action = dispatch(sendReminderEmail({
          name: selectedClient.full_name,
          email: selectedClient.email,
          days: selectedClient.days,
          pvc_number: selectedClient.pvc_number,
        }));
      }

      if (action) {
        const response = await action.unwrap();
      }

      // Close modal
      toggleModal();

      // Handle success message
      toast.success(`Email sent successfully to ${selectedClient.email}`, { autoClose: 1000 });
    } catch (error) {
      console.error("Error sending email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Full Name",
        accessorKey: "full_name",
        enableColumnFilter: false,
        headerStyle: { textAlign: "center" },
        cell: (cell) => <div className="text-center">{cell.getValue()}</div>,
      },
      {
        header: "PVC Number",
        accessorKey: "pvc_number",
        enableColumnFilter: false,
        headerStyle: { textAlign: "center" },
        cell: (cellProps) => {
          const contract = cellProps.row.original;
          return (
            <div className="text-center">
              <Link
                to={`/contracts/${contract.contract_id}`} // ✅ Uses contract_id
                className="badge bg-primary text-white px-3 py-2"
                style={{
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  transition: "0.3s ease-in-out",
                  borderRadius: "5px",
                }}
                aria-label={`View contract ${contract.pvc_number}`}
              >
                <i className="ri-file-list-3-line"></i> {/* 📜 Add an icon */}
                {contract.pvc_number}
              </Link>
            </div>
          );
        },
      },
      {
        header: "Units",
        accessorKey: "unit_details",
        enableColumnFilter: false,
        headerStyle: { textAlign: "center" },
        cell: (cell) => <div className="text-center">{cell.getValue()}</div>,
      },
      {
        header: "Unit Count",
        accessorKey: "unit_count",
        enableColumnFilter: false,
        headerStyle: { textAlign: "center" },
        cell: (cell) => <div className="text-center">{cell.getValue()}</div>,
      },
      {
        header: "Last Email",
        accessorKey: "last_email_sent_at",
        enableColumnFilter: false,
        headerStyle: { textAlign: "center" },
        cell: (cell) => <div className="text-center">{cell.getValue()}</div>,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        headerStyle: { textAlign: "center" },
        cell: (cell) => {
          const status = cell.getValue();
          let badgeClass = "";
          let iconClass = "";

          if (status.includes("Overdue")) {
            badgeClass =
              "badge bg-danger d-flex align-items-center justify-content-center";
            iconClass = "ri-error-warning-line fs-4";
          } else if (status.includes("Due")) {
            badgeClass =
              "badge bg-warning d-flex align-items-center justify-content-center";
            iconClass = "ri-alarm-warning-line fs-3";
          } else {
            badgeClass =
              "badge bg-success d-flex align-items-center justify-content-center";
            iconClass = "ri-check-line fs-5";
          }

          return (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                height: "100%",
              }}
            >
              <span
                className={badgeClass}
                style={{
                  width: "100%",
                  padding: "0.5rem 0.75rem",
                  borderRadius: "0.25rem",
                }}
              >
                <i className={`${iconClass} me-2`}></i>
                {status}
              </span>
            </div>
          );
        },
      },
      {
        header: "Action",
        cell: (cellProps) => {
          const client = cellProps.row.original;
          return (
            <div className="text-center">
              <button
                className="btn btn-info btn-sm"
                onClick={() => openSendReminderModal(client)}
              >
                Send Reminder
              </button>
            </div>
          );
        },
      },
    ],
    [openSendReminderModal]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="border-0">
                <h5 className="card-title mb-0">Overdue</h5>
              </CardHeader>
              <CardBody>
                {isClientSuccess ? (
                  contractsData.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={contractsData}
                      isGlobalFilter={true}
                      customPageSize={pageSize}
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light text-muted text-uppercase text-center"
                      SearchPlaceholder="Search contracts..."
                    />
                  ) : (
                    <div className="text-center my-5 py-5">
                      <div className="mb-3">
                        <i className="ri-checkbox-circle-line text-success display-4"></i>
                      </div>
                      <h5 className="text-success mb-2">All Caught Up!</h5>
                      <p className="text-muted">Great news! You have no overdue clients at the moment.</p>
                    </div>
                  )
                ) : (
                  <Loader error={error} />
                )}
                <ToastContainer closeButton={false} limit={1} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Send Reminder Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>
          Send Reminder
        </ModalHeader>
        <ModalBody>
          {selectedClient && (
            <div className="text-center">
              <p className="mb-4">
                Send reminder for PVC: <strong>{selectedClient.pvc_number}</strong>
              </p>
              <p className="mb-1">
                <strong>Client:</strong> {selectedClient.full_name}
              </p>
              <p className="mb-1">
                <strong>Email:</strong> {selectedClient.email}
              </p>
              <p className="mb-1">
                <strong>Status:</strong> {selectedClient.status}
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSendEmail}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Sending...
              </>
            ) : (
              "Send"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ClientManagement;